/**
 * datasource 用の asset fetch client に queue 制御、request timeout、429 retry を付与する。
 */

export type AssetFetchClient = {
  get: (path: string, timeout?: number) => Promise<Response>
}

type SemaphoreRelease = () => void

type SemaphoreAcquireOptions = {
  timeoutMs?: number
}

type SemaphoreQueueEntry = {
  resolve: (release: SemaphoreRelease) => void
  reject: (error: Error) => void
  clearTimer: () => void
}

type FetchImplementation = (
  input: string,
  init?: RequestInit,
) => Promise<Response>

type SleepImplementation = (durationMs: number) => Promise<void>

type RequestTimeoutOptions = {
  queueTimeoutMs: number
  requestTimeoutMs: number
}

type RunGetParameters = {
  path: string
  timeoutOptions: RequestTimeoutOptions
}

type RunFetchWithTimeoutParameters = {
  url: string
  requestTimeoutMs: number
  fetchImplementation: FetchImplementation
}

type CreateAssetFetchClientParameters = {
  endpoint: string
  concurrency?: number
  maxRetry?: number
  fetchImplementation?: FetchImplementation
  sleep?: SleepImplementation
}

const DEFAULT_CONCURRENCY = 100
const DEFAULT_MAX_RETRY = 3
const DEFAULT_RETRY_DELAY_MS = 1_000
const DEFAULT_TIMEOUT_MS = 300_000
const QUEUE_TIMEOUT_ERROR_MESSAGE = 'Timeout while waiting for slot'
const REQUEST_TIMEOUT_ERROR_MESSAGE = 'Timeout while fetching asset'

/**
 * queue wait timeout 用の Error を生成する。
 * @returns queue wait timeout を表す Error
 */
const createQueueTimeoutError = (): Error =>
  new Error(QUEUE_TIMEOUT_ERROR_MESSAGE)

/**
 * request timeout 用の Error を生成する。
 * @returns request timeout を表す Error
 */
const createRequestTimeoutError = (): Error =>
  new Error(REQUEST_TIMEOUT_ERROR_MESSAGE)

/**
 * AbortError を判定する。
 * @param error - 判定対象の error
 * @returns AbortError の場合は true
 */
const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  error.name === 'AbortError'

/**
 * timeout 値から queue / request 用の内部 option を生成する。
 * @param timeoutMs - public API から受け取る timeout ミリ秒
 * @returns queue と request の timeout option
 */
const createRequestTimeoutOptions = ({
  timeoutMs,
}: {
  timeoutMs: number
}): RequestTimeoutOptions => ({
  queueTimeoutMs: timeoutMs,
  requestTimeoutMs: timeoutMs,
})

/**
 * 既定の sleep 実装。
 * @param durationMs - 待機時間ミリ秒
 * @returns 指定時間待機した Promise
 */
const defaultSleep: SleepImplementation = async (
  durationMs: number,
): Promise<void> =>
  await new Promise<void>((resolve) => {
    setTimeout(resolve, durationMs)
  })

/**
 * 既定の fetch 実装。
 * @param input - fetch 対象 URL
 * @param init - fetch 初期化オプション
 * @returns fetch の Response
 */
const defaultFetchImplementation: FetchImplementation = async (
  input: string,
  init?: RequestInit,
): Promise<Response> => await fetch(input, init)

/**
 * permit 数を制限し、timeout 付き acquire を提供する semaphore。
 */
export class Semaphore {
  private permits: number
  private readonly queue: SemaphoreQueueEntry[] = []

  public constructor(permits: number) {
    this.permits = permits
  }

  /**
   * permit を 1 つ取得し、解放関数を返す。
   * @param options - acquire の timeout option
   * @returns permit を解放する idempotent な関数
   */
  public readonly acquire = async (
    options: SemaphoreAcquireOptions = {},
  ): Promise<SemaphoreRelease> => {
    if (this.permits > 0) {
      this.permits -= 1
      return this.createRelease()
    }

    return await new Promise<SemaphoreRelease>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined

      const entry: SemaphoreQueueEntry = {
        resolve,
        reject,
        clearTimer: () => {
          if (timeoutId !== undefined) {
            clearTimeout(timeoutId)
            timeoutId = undefined
          }
        },
      }

      if (options.timeoutMs !== undefined) {
        timeoutId = setTimeout(() => {
          if (!this.removeQueueEntry({ entry })) {
            return
          }

          entry.clearTimer()
          reject(createQueueTimeoutError())
        }, options.timeoutMs)
      }

      this.queue.push(entry)
    })
  }

  /**
   * queue 内の entry を除去する。
   * @param params - 除去対象の entry
   * @returns 除去できた場合は true
   */
  private readonly removeQueueEntry = ({
    entry,
  }: {
    entry: SemaphoreQueueEntry
  }): boolean => {
    const index = this.queue.indexOf(entry)

    if (index < 0) {
      return false
    }

    this.queue.splice(index, 1)
    return true
  }

  /**
   * permit を 1 つ解放する idempotent な関数を生成する。
   * @returns permit 解放関数
   */
  private readonly createRelease = (): SemaphoreRelease => {
    let released = false

    return (): void => {
      if (released) {
        return
      }

      released = true
      this.permits += 1
      this.drainQueue()
    }
  }

  /**
   * queue 先頭から、利用可能な permit 数だけ acquire を解決する。
   */
  private readonly drainQueue = (): void => {
    while (this.permits > 0 && this.queue.length > 0) {
      const entry = this.queue.shift()

      if (entry === undefined) {
        return
      }

      entry.clearTimer()
      this.permits -= 1
      entry.resolve(this.createRelease())
    }
  }
}

/**
 * request timeout ごとに AbortController を作成し、fetch を実行する。
 * @param params - fetch 実行に必要な引数
 * @returns fetch の Response
 */
const runFetchWithTimeout = async ({
  url,
  requestTimeoutMs,
  fetchImplementation,
}: RunFetchWithTimeoutParameters): Promise<Response> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    controller.abort()
  }, requestTimeoutMs)

  try {
    return await fetchImplementation(url, {
      signal: controller.signal,
      cache: 'no-store',
    })
  } catch (error) {
    if (isAbortError(error)) {
      throw createRequestTimeoutError()
    }

    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * asset fetch client を生成する。
 * @param params - endpoint と内部依存設定
 * @returns asset fetch client
 */
export const createAssetFetchClient = ({
  endpoint,
  concurrency = DEFAULT_CONCURRENCY,
  maxRetry = DEFAULT_MAX_RETRY,
  fetchImplementation = defaultFetchImplementation,
  sleep = defaultSleep,
}: CreateAssetFetchClientParameters): AssetFetchClient => {
  const semaphore = new Semaphore(concurrency)

  /**
   * timeout option を使って GET request を実行する。
   * @param params - request path と内部 timeout option
   * @returns fetch の Response
   */
  const runGet = async ({
    path,
    timeoutOptions,
  }: RunGetParameters): Promise<Response> => {
    for (let retryCount = 0; retryCount <= maxRetry; retryCount += 1) {
      const release = await semaphore.acquire({
        timeoutMs: timeoutOptions.queueTimeoutMs,
      })
      let shouldRetry = false

      try {
        const response = await runFetchWithTimeout({
          url: `${endpoint}${path}`,
          requestTimeoutMs: timeoutOptions.requestTimeoutMs,
          fetchImplementation,
        })

        if (response.status === 429) {
          if (retryCount >= maxRetry) {
            throw new Error(`${response.status}: ${response.statusText}`)
          }

          shouldRetry = true
        } else if (!response.ok && response.status !== 404) {
          throw new Error(`${response.status}: ${response.statusText}`)
        } else {
          return response
        }
      } finally {
        release()
      }

      if (shouldRetry) {
        await sleep(DEFAULT_RETRY_DELAY_MS)
      }
    }

    throw new Error('Failed to fetch asset after retries')
  }

  /**
   * public API を維持した GET request を実行する。
   * @param path - request path
   * @param timeout - queue wait と request に適用する timeout ミリ秒
   * @returns fetch の Response
   */
  const get = async (
    path: string,
    timeout = DEFAULT_TIMEOUT_MS,
  ): Promise<Response> =>
    await runGet({
      path,
      timeoutOptions: createRequestTimeoutOptions({
        timeoutMs: timeout,
      }),
    })

  return {
    get,
  }
}
